import React, { useState } from 'react';
import { PlusCircle, Building2 } from 'lucide-react';
import type { Expense, PaymentMethod, ExpenseStatus } from '@/types/budget';
import { getStoredVendors, subscribeToVendorUpdates } from '../../../utils/storage';

interface ExpenseFormProps {
  onSubmit: (expense: Omit<Expense, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onAddVendor: () => void;
}

const CATEGORIES = [
  'Food & Beverages',
  'Decorations',
  'Equipment',
  'Rentals',
  'Marketing',
  'Entertainment',
  'Other',
];

const PAYMENT_METHODS: PaymentMethod[] = ['cash', 'credit', 'check', 'other'];

const EXPENSE_STATUSES: ExpenseStatus[] = ['pending', 'approved', 'rejected'];

export function ExpenseForm({ onSubmit, onAddVendor }: ExpenseFormProps) {
  const [formData, setFormData] = useState({
    date: '',
    category: '',
    vendor: '',
    amount: '',
    description: '',
    paymentMethod: 'credit' as PaymentMethod,
    taxAmount: '',
    tags: '',
    status: 'pending' as ExpenseStatus,
  });

  const [vendors, setVendors] = useState(() => getStoredVendors());

  React.useEffect(() => {
    return subscribeToVendorUpdates((updatedVendors) => {
      setVendors(updatedVendors);
      // Select the most recently added vendor
      if (updatedVendors.length > 0) {
        const latestVendor = updatedVendors[updatedVendors.length - 1];
        setFormData(prev => ({ ...prev, vendor: latestVendor.name }));
      }
    });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // onSubmit({
    //   ...formData,
    //   amount: parseFloat(formData.amount),
    //   taxAmount: parseFloat(formData.taxAmount) || 0,
    //   status: 'pending',
    //   tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
    //   status: formData.status,
    // });
    // setFormData({
    //   date: '',
    //   category: '',
    //   vendor: '',
    //   amount: '',
    //   description: '',
    //   paymentMethod: 'credit',
    //   taxAmount: '',
    //   tags: '',
    //   status: 'pending',
    // });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Add New Expense</h2>
        <PlusCircle className="text-indigo-600 h-6 w-6" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Date</label>
          <input
            type="date"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={formData.date}
            onChange={e => setFormData(prev => ({ ...prev, date: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <select
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={formData.category}
            onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
          >
            <option value="">Select category</option>
            {CATEGORIES.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Vendor</label>
          <div className="flex space-x-2">
            <select
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={formData.vendor}
              onChange={e => setFormData(prev => ({ ...prev, vendor: e.target.value }))}
            >
              <option value="">Select vendor</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.name}>{vendor.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onAddVendor}
              className="mt-1 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Building2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Amount</label>
          <input
            type="number"
            required
            step="0.01"
            min="0"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={formData.amount}
            onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Tax Amount</label>
          <input
            type="number"
            step="0.01"
            min="0"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={formData.taxAmount}
            onChange={e => setFormData(prev => ({ ...prev, taxAmount: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Payment Method</label>
          <select
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={formData.paymentMethod}
            onChange={e => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
          >
            {PAYMENT_METHODS.map(method => (
              <option key={method} value={method}>{method.charAt(0).toUpperCase() + method.slice(1)}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Tags (comma-separated)</label>
          <input
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="urgent, reimbursement"
            value={formData.tags}
            onChange={e => setFormData(prev => ({ ...prev, tags: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={formData.status}
            onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as ExpenseStatus }))}
          >
            {EXPENSE_STATUSES.map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          rows={3}
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
        />
      </div>

      <button
        type="submit"
        className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      >
        Add Expense
      </button>
    </form>
  );
}